import { useMutation } from "@tanstack/react-query";
import { Download, FileWarning, Loader2, ShieldAlert, Upload } from "lucide-react";
import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";

const RESTORE_CONFIRM_PHRASE = "CARREGAR";

interface DumpPreview {
  version?: number;
  takenAt?: string;
  source?: string;
  scoringConfig?: unknown[];
  calibrationFixtures?: unknown[];
  configVersions?: unknown[];
  labsEvaluations?: unknown[];
  candidateCorrections?: unknown[];
}

/**
 * Engineering-tier backup/restore for the five labs tables. Lives under
 * "IV · engenharia" so an operator who never touches dumps doesn't have
 * to scroll past it on the Calibração screen, while engineers know
 * exactly where to find it. Both paths require labs access; restore
 * runs in any environment, with the typed "CARREGAR" phrase as the
 * destructive-action confirmation.
 */
export function BackupSection() {
  const { toast } = useToast();
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const [pendingRestore, setPendingRestore] = useState<{
    payload: DumpPreview;
    fileName: string;
  } | null>(null);
  const [restoreConfirmToken, setRestoreConfirmToken] = useState("");

  const downloadDump = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/labs/data/dump", { credentials: "include" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // Browser saves the response under the filename the controller picks
      // via Content-Disposition. We blob-and-anchor here to make it work
      // identically across Chromium/Firefox without relying on each
      // browser's auto-download policy.
      const dispo = res.headers.get("content-disposition") ?? "";
      const match = /filename="([^"]+)"/.exec(dispo);
      const filename =
        match?.[1] ?? `labs-dump-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { filename };
    },
    onSuccess: ({ filename }) => {
      toast({ title: "Dump baixado", description: filename });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao baixar dump",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const restoreDump = useMutation({
    mutationFn: async (payload: unknown) => {
      return rpcClient.labs.dataDumpRestore(payload as Record<string, unknown>) as Promise<{
        ok?: boolean;
        scoringConfig?: number;
        calibrationFixtures?: number;
        configVersions?: number;
        labsEvaluations?: number;
        candidateCorrections?: number;
      }>;
    },
    onSuccess: (result) => {
      setPendingRestore(null);
      setRestoreConfirmToken("");
      queryClient.invalidateQueries({ queryKey: ["labs"] });
      const counts = [
        ["scoring_config", result.scoringConfig],
        ["calibration_fixtures", result.calibrationFixtures],
        ["config_versions", result.configVersions],
        ["labs_evaluations", result.labsEvaluations],
        ["candidate_corrections", result.candidateCorrections],
      ]
        .filter(([, n]) => typeof n === "number" && (n as number) > 0)
        .map(([k, n]) => `${n} ${k}`)
        .join(" · ");
      toast({
        title: "Dump carregado",
        description: counts || "Sem linhas no dump.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao carregar dump",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="py-4 space-y-4" data-testid="labs-backup-section">
      <p className="text-sm text-muted-foreground max-w-prose">
        Baixa um snapshot JSON das cinco tabelas do labs (
        <code className="mono-inline">scoring_config</code>,{" "}
        <code className="mono-inline">calibration_fixtures</code>,{" "}
        <code className="mono-inline">config_versions</code>,{" "}
        <code className="mono-inline">labs_evaluations</code>,{" "}
        <code className="mono-inline">candidate_corrections</code>) ou carrega um snapshot
        previamente baixado. Útil para reproduzir localmente um problema visto em produção sem
        precisar de pipe direto ao Postgres, ou para carregar dados de dev em produção em uma
        migração one-shot. Restaurar exige acesso ao labs e a confirmação digitada.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => downloadDump.mutate()}
          disabled={downloadDump.isPending}
          data-testid="dump-download"
        >
          {downloadDump.isPending ? (
            <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5 mr-2" />
          )}
          Baixar dump de labs
        </Button>
        <input
          ref={restoreInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            try {
              const text = await file.text();
              const parsed = JSON.parse(text) as DumpPreview;
              setPendingRestore({ payload: parsed, fileName: file.name });
              setRestoreConfirmToken("");
            } catch (err) {
              toast({
                title: "Arquivo inválido",
                description: err instanceof Error ? err.message : String(err),
                variant: "destructive",
              });
            }
          }}
          data-testid="dump-restore-input"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => restoreInputRef.current?.click()}
          disabled={restoreDump.isPending}
          data-testid="dump-restore-trigger"
        >
          <Upload className="h-3.5 w-3.5 mr-2" />
          Carregar dump local
        </Button>
        <span className="text-xs text-muted-foreground">
          o dump baixa direto · carregar pede confirmação digitada
        </span>
      </div>

      <AlertDialog
        open={pendingRestore !== null}
        onOpenChange={(v) => {
          if (!v && !restoreDump.isPending) {
            setPendingRestore(null);
            setRestoreConfirmToken("");
          }
        }}
      >
        <AlertDialogContent className="border-rose-200 bg-rose-50/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldAlert className="h-5 w-5" aria-hidden />
              Carregar dump de labs?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-1">
                <p className="text-sm text-foreground/85">
                  Esta ação <strong>apaga toda a base de labs</strong> deste ambiente e reescreve
                  com o conteúdo do arquivo <code>{pendingRestore?.fileName}</code>. Roda em
                  qualquer ambiente — não há gate por NODE_ENV.
                </p>
                {pendingRestore && (
                  <ul className="reset-effects-list">
                    <li>
                      <FileWarning className="h-3.5 w-3.5 text-rose-600 shrink-0" aria-hidden />
                      <span>
                        dump v{pendingRestore.payload.version ?? "?"} · origem{" "}
                        <code>{pendingRestore.payload.source ?? "(desconhecida)"}</code> · tirado em{" "}
                        {pendingRestore.payload.takenAt
                          ? new Date(pendingRestore.payload.takenAt).toLocaleString("pt-BR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </span>
                    </li>
                    <li>
                      <FileWarning className="h-3.5 w-3.5 text-rose-600 shrink-0" aria-hidden />
                      <span>
                        {pendingRestore.payload.scoringConfig?.length ?? 0} scoring_config ·{" "}
                        {pendingRestore.payload.calibrationFixtures?.length ?? 0}{" "}
                        calibration_fixtures · {pendingRestore.payload.configVersions?.length ?? 0}{" "}
                        config_versions · {pendingRestore.payload.labsEvaluations?.length ?? 0}{" "}
                        labs_evaluations ·{" "}
                        {pendingRestore.payload.candidateCorrections?.length ?? 0}{" "}
                        candidate_corrections
                      </span>
                    </li>
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">
                  Para confirmar, digite{" "}
                  <strong className="font-mono">{RESTORE_CONFIRM_PHRASE}</strong> no campo abaixo:
                </p>
                <Input
                  autoFocus
                  value={restoreConfirmToken}
                  onChange={(e) => setRestoreConfirmToken(e.target.value)}
                  placeholder={RESTORE_CONFIRM_PHRASE}
                  className="font-mono tracking-wider"
                  disabled={restoreDump.isPending}
                  data-testid="dump-restore-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreDump.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingRestore && restoreConfirmToken === RESTORE_CONFIRM_PHRASE) {
                  restoreDump.mutate(pendingRestore.payload);
                }
              }}
              disabled={restoreDump.isPending || restoreConfirmToken !== RESTORE_CONFIRM_PHRASE}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="dump-restore-confirm"
            >
              {restoreDump.isPending ? "Carregando…" : "Carregar e substituir tudo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
